// @djsem/analysis-contract — AI Generation QA, Review, Brand Consistency, Rights Review (Prompt 22D).
//
// Review layer that sits between AI generation output and Release Studio / Campaign Packs.
// No auto-approval of unknown-rights for public/commercial use.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** Overall human-review status of an AI-generated asset. */
export type DJSEMAIGenerationReviewStatus =
  | "not_reviewed" | "needs_review" | "approved" | "approved_with_warnings" | "needs_edit" | "rejected" | "archived";

/** Provider content-moderation status for an AI-generated asset. */
export type DJSEMAIModerationStatus =
  | "not_available" | "not_checked" | "passed" | "flagged" | "blocked";

/** Result of checking a generated asset against Brand World directives/restricted elements. */
export interface DJSEMBrandConsistencyReview {
  brandWorldId?: string;
  score: number; // 0..1
  status: "not_checked" | "pass" | "warn" | "fail";
  matchedDirectives: string[];
  missingDirectives: string[];
  conflicts: string[];
  warnings: string[];
}

/** Result of checking a generated asset's technical properties against expected export presets. */
export interface DJSEMGeneratedAssetTechnicalReview {
  status: "not_checked" | "pass" | "warn" | "fail";
  width?: number; height?: number;
  durationMs?: number; fps?: number;
  format?: string; fileSizeBytes?: number; hash?: string;
  matchesExportPreset: boolean;
  issues: string[];
}

/** Result of checking a generated asset's rights status for public/commercial use. */
export interface DJSEMGeneratedAssetRightsReview {
  status: "unknown" | "owned" | "licensed" | "restricted";
  publicUseAllowed: boolean;
  commercialExportAllowed: boolean;
  confirmationRequired: boolean;
  confirmations: string[];
  warnings: string[];
}

/** Result of attaching provider content-moderation metadata to a generated asset. */
export interface DJSEMGeneratedAssetModerationReview {
  status: DJSEMAIModerationStatus;
  providerModerationRef?: string;
  flags: string[];
  warnings: string[];
}

/** The full review record for a single AI-generated asset variant (brand + technical + rights + moderation). */
export interface DJSEMAIGenerationReview {
  id: string;
  projectId: string;
  assetId: string;
  assetVariantId: string;
  generationJobId: string;
  promptVersionId?: string;
  providerId?: string;
  outputKind: "artwork" | "video";
  reviewStatus: DJSEMAIGenerationReviewStatus;
  brandReview: DJSEMBrandConsistencyReview;
  technicalReview: DJSEMGeneratedAssetTechnicalReview;
  rightsReview: DJSEMGeneratedAssetRightsReview;
  moderationReview: DJSEMGeneratedAssetModerationReview;
  reviewerNotes: string[];
  warnings: string[];
  createdAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link AIGenerationReviewService.createReview}. */
export interface CreateAIGenerationReviewInput {
  projectId: string;
  assetId: string;
  assetVariantId: string;
  generationJobId: string;
  promptVersionId?: string;
  providerId?: string;
  outputKind: "artwork" | "video";
  createdAt?: string;
}

/** Input for {@link AIGenerationReviewService.runTechnicalReview}. */
export interface RunTechnicalReviewInput {
  review: DJSEMAIGenerationReview;
  actualWidth?: number; actualHeight?: number;
  actualDurationMs?: number; actualFps?: number;
  actualFormat?: string; actualFileSizeBytes?: number;
  actualHash?: string;
  hasOutputFile: boolean;
  expectedWidth?: number; expectedHeight?: number;
  expectedFormat?: string;
}

/** Input for {@link AIGenerationReviewService.runBrandConsistencyReview}. */
export interface RunBrandReviewInput {
  review: DJSEMAIGenerationReview;
  brandWorldId?: string;
  brandDirectives: string[];
  restrictedElements: string[];
  promptUsedDirectives: string[];
}

/** Input for {@link AIGenerationReviewService.runRightsReview}. */
export interface RunRightsReviewInput {
  review: DJSEMAIGenerationReview;
  sourceRightsStatus?: "unknown" | "owned" | "licensed" | "restricted";
  hasThirdPartyLogo?: boolean;
  hasThirdPartyLikeness?: boolean;
  intendedForPublicUse?: boolean;
  intendedForCommercialExport?: boolean;
}

/** Input for {@link AIGenerationReviewService.attachModerationMetadata}. */
export interface AttachModerationMetadataInput {
  review: DJSEMAIGenerationReview;
  providerModerationRef?: string;
  status?: DJSEMAIModerationStatus;
  flags?: string[];
}

/** Input for {@link AIGenerationReviewService.approveOutput}. */
export interface ApproveGeneratedOutputInput { review: DJSEMAIGenerationReview; reviewedBy?: string; reviewedAt?: string; notes?: string[]; }
/** Input for {@link AIGenerationReviewService.rejectOutput}. */
export interface RejectGeneratedOutputInput { review: DJSEMAIGenerationReview; reviewedBy?: string; reviewedAt?: string; notes?: string[]; reason: string; }
/** Input for {@link AIGenerationReviewService.markNeedsEdit}. */
export interface MarkNeedsEditInput { review: DJSEMAIGenerationReview; reviewedBy?: string; notes?: string[]; }
/** Input for {@link AIGenerationReviewService.validateReleaseReadiness}. */
export interface ValidateReleaseReadinessInput { review: DJSEMAIGenerationReview; }

// ---- helpers ----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

function defaultBrandReview(): DJSEMBrandConsistencyReview {
  return { score: 0, status: "not_checked", matchedDirectives: [], missingDirectives: [], conflicts: [], warnings: [] };
}
function defaultTechReview(): DJSEMGeneratedAssetTechnicalReview {
  return { status: "not_checked", matchesExportPreset: false, issues: [] };
}
function defaultRightsReview(): DJSEMGeneratedAssetRightsReview {
  return { status: "unknown", publicUseAllowed: false, commercialExportAllowed: false, confirmationRequired: true, confirmations: [], warnings: ["Rights status unknown — review required before public/commercial use."] };
}
function defaultModerationReview(): DJSEMGeneratedAssetModerationReview {
  return { status: "not_available", flags: [], warnings: ["Provider moderation metadata unavailable. Manual review required before public/commercial use."] };
}

// ---- service ----------------------------------------------------------------

/** Review layer between AI generation output and Release Studio / Campaign Packs: technical, brand, rights, and moderation checks with gated approval. */
export class AIGenerationReviewService {
  /**
   * Create a new review record for a generated asset variant, defaulted to "needs_review".
   * @param input - the asset/job/provider identifiers to review.
   * @returns the created review.
   */
  createReview(input: CreateAIGenerationReviewInput): DJSEMAIGenerationReview {
    return {
      id: `rev_${_seq.next()}_${input.assetVariantId}`,
      projectId: input.projectId,
      assetId: input.assetId,
      assetVariantId: input.assetVariantId,
      generationJobId: input.generationJobId,
      promptVersionId: input.promptVersionId,
      providerId: input.providerId,
      outputKind: input.outputKind,
      reviewStatus: "needs_review",
      brandReview: defaultBrandReview(),
      technicalReview: defaultTechReview(),
      rightsReview: defaultRightsReview(),
      moderationReview: defaultModerationReview(),
      reviewerNotes: [],
      warnings: [],
      createdAt: input.createdAt ?? AT,
    };
  }

  /**
   * Check a generated asset's actual technical properties against expected export preset values.
   * @param input - the review, actual measured properties, and expected preset values.
   * @returns the technical review result (pass/warn/fail).
   */
  runTechnicalReview(input: RunTechnicalReviewInput): DJSEMGeneratedAssetTechnicalReview {
    const issues: string[] = [];
    if (!input.hasOutputFile) {
      return { status: "fail", matchesExportPreset: false, issues: ["Output file missing — cannot approve."] };
    }
    let preset = true;
    if (input.expectedWidth && input.actualWidth && input.actualWidth !== input.expectedWidth) { issues.push(`width: expected ${input.expectedWidth}, got ${input.actualWidth}`); preset = false; }
    if (input.expectedHeight && input.actualHeight && input.actualHeight !== input.expectedHeight) { issues.push(`height: expected ${input.expectedHeight}, got ${input.actualHeight}`); preset = false; }
    if (input.expectedFormat && input.actualFormat && input.actualFormat !== input.expectedFormat) { issues.push(`format: expected ${input.expectedFormat}, got ${input.actualFormat}`); preset = false; }
    if (input.actualDurationMs !== undefined && input.actualDurationMs < 500) issues.push("video duration suspiciously short");
    const status = issues.length === 0 ? "pass" : issues.some(i => /missing/i.test(i)) ? "fail" : "warn";
    return {
      status, width: input.actualWidth, height: input.actualHeight,
      durationMs: input.actualDurationMs, fps: input.actualFps,
      format: input.actualFormat, fileSizeBytes: input.actualFileSizeBytes,
      hash: input.actualHash, matchesExportPreset: preset, issues,
    };
  }

  /**
   * Score a generated asset against a Brand World's directives and restricted elements.
   * @param input - the review, brand world id, directive/restriction lists, and directives used in the prompt.
   * @returns the brand consistency review (score + matched/missing directives + conflicts).
   */
  runBrandConsistencyReview(input: RunBrandReviewInput): DJSEMBrandConsistencyReview {
    if (!input.brandWorldId) return { ...defaultBrandReview(), status: "not_checked", warnings: ["No Brand World specified — brand consistency not evaluated."] };
    const matched = input.promptUsedDirectives.filter(d => input.brandDirectives.includes(d));
    const missing = input.brandDirectives.filter(d => !input.promptUsedDirectives.includes(d));
    const conflicts: string[] = [];
    for (const r of input.restrictedElements) {
      if (input.promptUsedDirectives.some(d => d.toLowerCase().includes(r.toLowerCase()))) {
        conflicts.push(`restricted element used: ${r}`);
      }
    }
    const score = input.brandDirectives.length > 0 ? matched.length / input.brandDirectives.length : 1;
    const status = conflicts.length > 0 ? "fail" : score >= 0.7 ? "pass" : score >= 0.3 ? "warn" : "fail";
    return { brandWorldId: input.brandWorldId, score, status, matchedDirectives: matched, missingDirectives: missing, conflicts, warnings: conflicts.length > 0 ? ["Brand World restricted element violated."] : [] };
  }

  /**
   * Determine public/commercial-use eligibility for a generated asset based on source rights and detected third-party content.
   * @param input - the review plus source rights status and third-party logo/likeness flags.
   * @returns the rights review result; unknown/restricted rights block public/commercial use.
   */
  runRightsReview(input: RunRightsReviewInput): DJSEMGeneratedAssetRightsReview {
    const warnings: string[] = [];
    const status = input.sourceRightsStatus ?? "unknown";
    let publicOk = status === "owned" || status === "licensed";
    let commercialOk = publicOk;
    let confirmNeeded = false;
    if (input.hasThirdPartyLogo) { warnings.push("Third-party logo detected — requires rights confirmation."); confirmNeeded = true; publicOk = false; commercialOk = false; }
    if (input.hasThirdPartyLikeness) { warnings.push("Third-party likeness detected — requires rights confirmation."); confirmNeeded = true; publicOk = false; commercialOk = false; }
    if (status === "unknown") { warnings.push("Source rights unknown — confirmation required."); confirmNeeded = true; }
    if (status === "restricted") { warnings.push("Restricted rights — public/commercial use blocked."); publicOk = false; commercialOk = false; }
    if (input.intendedForCommercialExport && !commercialOk) warnings.push("Commercial export intended but rights not confirmed.");
    return { status, publicUseAllowed: publicOk, commercialExportAllowed: commercialOk, confirmationRequired: confirmNeeded, confirmations: [], warnings };
  }

  /**
   * Attach provider content-moderation metadata to a review.
   * @param input - the review and the provider's moderation status/flags.
   * @returns the moderation review (defaults to "not_available" with a manual-review warning).
   */
  attachModerationMetadata(input: AttachModerationMetadataInput): DJSEMGeneratedAssetModerationReview {
    if (!input.status || input.status === "not_available") return defaultModerationReview();
    return {
      status: input.status,
      providerModerationRef: input.providerModerationRef,
      flags: input.flags ?? [],
      warnings: input.status === "flagged" ? ["Provider flagged content — manual review required."] : input.status === "blocked" ? ["Provider blocked content."] : [],
    };
  }

  /**
   * Approve a generated output, blocking approval if technical/rights/moderation checks failed.
   * @param input - the review, reviewer, and optional notes.
   * @returns the review updated to "approved" or "approved_with_warnings"; unchanged (with a warning) if blockers remain.
   */
  approveOutput(input: ApproveGeneratedOutputInput): DJSEMAIGenerationReview {
    // Defence-in-depth: block approval if tech review failed or rights restricted
    const hasBlockers = input.review.technicalReview.status === "fail" ||
      input.review.rightsReview.status === "restricted" ||
      input.review.moderationReview.status === "blocked";
    if (hasBlockers) {
      const warnings = [...input.review.warnings, "Cannot approve: blocking issues remain."];
      return { ...input.review, warnings };
    }
    const hasWarnings = input.review.brandReview.status === "warn" ||
      input.review.technicalReview.status === "warn" ||
      input.review.rightsReview.status === "unknown" ||
      input.review.moderationReview.status === "flagged";
    return {
      ...input.review,
      reviewStatus: hasWarnings ? "approved_with_warnings" : "approved",
      reviewedBy: input.reviewedBy,
      reviewedAt: input.reviewedAt ?? AT,
      reviewerNotes: [...input.review.reviewerNotes, ...(input.notes ?? [])],
    };
  }

  /**
   * Reject a generated output with a required reason.
   * @param input - the review, reviewer, reason, and optional notes.
   * @returns the review updated to "rejected".
   */
  rejectOutput(input: RejectGeneratedOutputInput): DJSEMAIGenerationReview {
    return {
      ...input.review,
      reviewStatus: "rejected",
      reviewedBy: input.reviewedBy,
      reviewedAt: input.reviewedAt ?? AT,
      reviewerNotes: [...input.review.reviewerNotes, input.reason, ...(input.notes ?? [])],
    };
  }

  /**
   * Mark a generated output as needing edits.
   * @param input - the review, reviewer, and optional notes.
   * @returns the review updated to "needs_edit".
   */
  markNeedsEdit(input: MarkNeedsEditInput): DJSEMAIGenerationReview {
    return { ...input.review, reviewStatus: "needs_edit", reviewedBy: input.reviewedBy, reviewerNotes: [...input.review.reviewerNotes, ...(input.notes ?? [])] };
  }

  /**
   * Validate that a review qualifies its asset for release (approved, no blocking failures, prompt provenance present).
   * @param input - the review to validate.
   * @returns validation result describing any release blockers.
   */
  validateReleaseReadiness(input: ValidateReleaseReadinessInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.review.reviewStatus !== "approved" && input.review.reviewStatus !== "approved_with_warnings")
      issues.push({ path: "$.reviewStatus", message: "asset must be approved" });
    if (input.review.technicalReview.status === "fail") issues.push({ path: "$.technicalReview", message: "technical review failed" });
    if (input.review.rightsReview.status === "restricted") issues.push({ path: "$.rightsReview", message: "restricted rights block release" });
    if (input.review.moderationReview.status === "blocked") issues.push({ path: "$.moderationReview", message: "moderation blocked" });
    if (!input.review.promptVersionId) issues.push({ path: "$.promptVersionId", message: "prompt provenance missing — required for release" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "release_readiness");
  }
}
